import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../../lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

export type AgentType =
  | 'PM_AGENT'
  | 'ARCHITECTURE_AGENT'
  | 'UX_AGENT'
  | 'SPRINT_AGENT'
  | 'DEV_AGENT'
  | 'REVIEW_AGENT'
  | 'QA_AGENT'
  | 'DEVOPS_AGENT'
  | 'RETRO_AGENT'
  | 'PHASE_GATE_AGENT'
  | 'BOOTSTRAP_AGENT';

export interface AgentConfigEntry {
  agentType:            AgentType;
  isActive:             boolean;
  systemPromptOverride: string | null;
  checklistDefaults:    string[] | null;
}

export interface AgentConfigData {
  configs: AgentConfigEntry[];
}

interface Props {
  tenantId:       string;
  /** Wizard advance callback. Optional so the settings surface can mount the
   *  component without a step-advance handler (it uses onSaved instead). */
  onStepComplete?: (data: AgentConfigData) => void;
  /** Called after a successful POST. Settings wires this to its saved flash;
   *  the wizard relies on onStepComplete to advance. Both fire on success. */
  onSaved?:       (data: AgentConfigData) => void;
  savedData?:     AgentConfigData;
  /** Submit button label. Wizard: "Save & Continue →"; settings: "Save changes". */
  submitLabel?:   string;
  /** When false, the form is read-only — inputs disabled, submit hidden. The
   *  authoritative gate is the server's requireRole('TENANT_ADMIN') on POST;
   *  this enforces the read-only invariant at the JS layer too. Default true. */
  canEdit?:       boolean;
  /** When true, renders a static, non-writing pointer telling the user that
   *  per-agent model selection is governed separately (Settings → Agent Model
   *  Configuration). CFGIA.B1 D12: the wizard no longer writes a per-agent
   *  model — the inert tenant_agent_configs.llm_* columns were dropped — so it
   *  shows this pointer instead of an editable dropdown. The Settings Agents
   *  surface omits it (default false) — it has its own HelpPopover. */
  showModelPointer?: boolean;
  /** When true (default) renders the "Agent Configuration" heading + intro.
   *  Surfaces with their own page-level <h1> (settings) pass false to avoid a
   *  duplicate title / skipped heading level. Mirrors QAPolicyForm.showIntro.
   *  Default true keeps the wizard step's heading unchanged. */
  showIntro?:     boolean;
}

// ── Phase definitions ─────────────────────────────────────────────────────────

interface PhaseRow {
  phase:      number;
  phaseLabel: string;
  agentType:  AgentType;
  agentLabel: string;
  core:       boolean;
}

const PHASE_ROWS: PhaseRow[] = [
  { phase: 1,   phaseLabel: 'Requirements',    agentType: 'PM_AGENT',           agentLabel: 'PM Agent',           core: true },
  { phase: 2,   phaseLabel: 'Architecture',    agentType: 'ARCHITECTURE_AGENT', agentLabel: 'Architecture Agent', core: true },
  { phase: 2.5, phaseLabel: 'UX Design',       agentType: 'UX_AGENT',           agentLabel: 'UX Design Agent',    core: true },
  { phase: 3,   phaseLabel: 'Sprint Planning', agentType: 'SPRINT_AGENT',       agentLabel: 'Sprint Agent',       core: true },
  { phase: 4,   phaseLabel: 'Development',     agentType: 'DEV_AGENT',          agentLabel: 'Dev Agent',          core: false },
  { phase: 5,   phaseLabel: 'Code Review',     agentType: 'REVIEW_AGENT',       agentLabel: 'Review Agent',       core: false },
  { phase: 6,   phaseLabel: 'QA & Testing',    agentType: 'QA_AGENT',           agentLabel: 'QA Agent',           core: false },
  { phase: 7,   phaseLabel: 'Deployment',      agentType: 'DEVOPS_AGENT',       agentLabel: 'DevOps Agent',       core: false },
  { phase: 8,   phaseLabel: 'Retrospective',   agentType: 'RETRO_AGENT',        agentLabel: 'Retro Agent',        core: false },
];

const ALL_AGENT_TYPES: AgentType[] = [
  'PM_AGENT', 'ARCHITECTURE_AGENT', 'UX_AGENT', 'SPRINT_AGENT', 'DEV_AGENT',
  'REVIEW_AGENT', 'QA_AGENT', 'DEVOPS_AGENT', 'RETRO_AGENT',
  'PHASE_GATE_AGENT', 'BOOTSTRAP_AGENT',
];

// ── Prompt templates & examples ──────────────────────────────────────────────

const PROMPT_TEMPLATES: Array<{ label: string; value: string }> = [
  {
    label: 'Healthcare',
    value: 'Ensure all outputs comply with HIPAA requirements. Flag any PII or PHI in artefacts. Require audit trails for data-access decisions. Prefer conservative architecture patterns for regulated workloads.',
  },
  {
    label: 'BFSI',
    value: 'Follow financial services regulatory guidelines (SOX, PCI-DSS). All transaction-related code must include idempotency. Require formal change-management sign-off references in artefacts.',
  },
  {
    label: 'General SaaS',
    value: 'Prioritise multi-tenant isolation in architecture decisions. Ensure horizontal scalability. Prefer event-driven patterns where applicable. Include observability (metrics, logs, traces) in every service.',
  },
];

const PROMPT_EXAMPLES: Array<{ domain: string; text: string }> = [
  {
    domain: 'Healthcare',
    text: '"Ensure all API responses strip PHI fields unless the caller has HIPAA-authorized scope."',
  },
  {
    domain: 'Fintech',
    text: '"Every database migration must be backwards-compatible for zero-downtime deploys."',
  },
  {
    domain: 'E-commerce',
    text: '"All pricing calculations must use integer cents to avoid floating-point rounding."',
  },
];

// ── Checklist suggestions & starter sets ─────────────────────────────────────

const CHECKLIST_SUGGESTIONS: Record<string, string[]> = {
  'Requirements': [
    'All user stories have acceptance criteria',
    'Non-functional requirements documented',
    'Stakeholder sign-off recorded',
  ],
  'Architecture': [
    'SAD reviewed by senior engineer',
    'ADRs created for key decisions',
    'Security threat model completed',
  ],
  'Development': [
    'All PRs reference a ticket number',
    'Unit test coverage above 80%',
    'No secrets committed to repository',
  ],
  'Deployment': [
    'Rollback plan documented',
    'Health check endpoints verified',
    'Monitoring alerts configured',
  ],
};

const HEALTHCARE_STARTER_SET = [
  'HIPAA compliance review completed',
  'PHI data flow audit documented',
  'BAA references attached to vendor integrations',
  'Access control matrix reviewed',
];

const BFSI_STARTER_SET = [
  'SOX controls evidence captured',
  'PCI-DSS scope boundaries documented',
  'Transaction idempotency verified',
  'Formal change-management approval recorded',
];

// ── Shared styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// ── InfoTooltip ──────────────────────────────────────────────────────────────

function InfoTooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, flipped: false });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    // Flip below if trigger is within 80px of the viewport top
    const flipped = rect.top < 80;
    setPos({
      top: flipped ? rect.bottom + 8 : rect.top - 8,
      left: centerX,
      flipped,
    });
    setVisible(true);
  };

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, cursor: 'help' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setVisible(false)}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ color: 'var(--text-secondary)' }}>
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" />
        <text x="7" y="10.5" textAnchor="middle" fill="currentColor" fontSize="9" fontWeight="600">i</text>
      </svg>
      {visible && createPortal(
        <div style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          transform: pos.flipped ? 'translateX(-50%)' : 'translate(-50%, -100%)',
          zIndex: 9999,
          padding: '8px 12px', borderRadius: 'var(--radius-sm)',
          background: 'var(--text)', color: 'var(--surface)', fontSize: 12,
          fontFamily: 'var(--font-body)', fontWeight: 400, lineHeight: 1.4,
          whiteSpace: 'normal', width: 240,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', textTransform: 'none', letterSpacing: 'normal',
          pointerEvents: 'none',
        }}>
          {text}
          {/* Arrow: points down when above, points up when flipped below */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            ...(pos.flipped
              ? { bottom: '100%', borderBottom: '5px solid var(--text)' }
              : { top: '100%', borderTop: '5px solid var(--text)' }),
          }} />
        </div>,
        document.body,
      )}
    </span>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
  title,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <div
      title={title}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11,
        background: checked ? 'var(--primary)' : 'var(--border)',
        position: 'relative', flexShrink: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 150ms',
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 150ms', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }} />
    </div>
  );
}

// ── Tag input ─────────────────────────────────────────────────────────────────

function TagInput({
  tags,
  onChange,
  placeholder,
  disabled,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    if (disabled) return;
    const trimmed = draft.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setDraft('');
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: tags.length ? 8 : 0 }}>
        {tags.map(tag => (
          <span
            key={tag}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px 3px 10px',
              background: 'rgba(124,58,237,0.08)',
              border: '1px solid rgba(124,58,237,0.2)',
              borderRadius: 20,
              fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)',
            }}
          >
            {tag}
            <button
              type="button"
              disabled={disabled}
              onClick={() => { if (!disabled) onChange(tags.filter(t => t !== tag)); }}
              style={{
                background: 'none', border: 'none',
                cursor: disabled ? 'default' : 'pointer',
                padding: '0 0 0 2px', color: 'var(--primary)', fontSize: 14,
                lineHeight: 1, display: 'flex', alignItems: 'center',
                opacity: disabled ? 0.5 : 1,
              }}
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={draft}
        disabled={disabled}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder={placeholder ?? 'Type and press Enter to add…'}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          background: 'var(--surface-alt)',
          color: 'var(--text)',
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          outline: 'none',
          boxSizing: 'border-box',
          opacity: disabled ? 0.6 : 1,
        }}
      />
    </div>
  );
}

// ── Accordion ─────────────────────────────────────────────────────────────────

function AccordionSection({
  label,
  agentPhaseLabel,
  promptOverride,
  onPromptChange,
  checklistItems,
  onChecklistChange,
  canEdit,
}: {
  label:              string;
  agentPhaseLabel:    string;
  promptOverride:     string;
  onPromptChange:     (v: string) => void;
  checklistItems:     string[];
  onChecklistChange:  (v: string[]) => void;
  canEdit:            boolean;
}) {
  const [open, setOpen] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const remaining = 2000 - promptOverride.length;

  const suggestions = CHECKLIST_SUGGESTIONS[agentPhaseLabel] ?? [];

  const addSuggestion = (item: string) => {
    if (!checklistItems.includes(item)) {
      onChecklistChange([...checklistItems, item]);
    }
  };

  const applyStarterSet = (set: string[]) => {
    const merged = [...checklistItems];
    for (const item of set) {
      if (!merged.includes(item)) merged.push(item);
    }
    onChecklistChange(merged);
  };

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          background: open ? 'rgba(124,58,237,0.04)' : 'var(--surface-alt)',
          border: 'none', cursor: 'pointer',
          fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
          color: 'var(--text)', textAlign: 'left',
        }}
      >
        <span>{label} — Advanced</span>
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms', flexShrink: 0 }}
        >
          <path d="M2 5l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{ padding: '14px 14px 16px', borderTop: '1px solid var(--border)' }}>
          {/* System prompt prefix */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Custom system prompt prefix (optional)
              <InfoTooltip text="This text is prepended to the agent's built-in system prompt. Use it to inject domain-specific rules, compliance requirements, or coding standards that every session should follow." />
            </label>
            <textarea
              value={promptOverride}
              disabled={!canEdit}
              onChange={e => onPromptChange(e.target.value.slice(0, 2000))}
              placeholder="Add instructions that will be prepended to this agent's default system prompt…"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--surface-alt)',
                color: 'var(--text)',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
                opacity: canEdit ? 1 : 0.6,
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Leave blank to use BluePrint&apos;s default prompt.
              </span>
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 12,
                color: remaining < 100 ? 'var(--warning)' : 'var(--text-secondary)',
              }}>
                {remaining} chars remaining
              </span>
            </div>

            {/* Use template dropdown */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                Use template:
              </span>
              {PROMPT_TEMPLATES.map(tpl => (
                <button
                  key={tpl.label}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => { if (canEdit) onPromptChange(tpl.value); }}
                  style={{
                    padding: '3px 10px', borderRadius: 4,
                    border: '1px solid var(--border)', background: 'var(--surface-alt)',
                    fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)',
                    cursor: canEdit ? 'pointer' : 'default',
                    opacity: canEdit ? 1 : 0.5,
                  }}
                >
                  {tpl.label}
                </button>
              ))}
            </div>

            {/* See examples collapsible */}
            <button
              type="button"
              onClick={() => setShowExamples(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                color: 'var(--primary)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {showExamples ? 'Hide examples' : 'See examples'}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                style={{ transform: showExamples ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}>
                <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showExamples && (
              <div style={{
                marginTop: 6, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                background: 'rgba(124,58,237,0.03)', border: '1px solid rgba(124,58,237,0.1)',
              }}>
                {PROMPT_EXAMPLES.map(ex => (
                  <div key={ex.domain} style={{ marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                      {ex.domain}
                    </span>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', margin: '2px 0 0', fontStyle: 'italic' }}>
                      {ex.text}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Checklist items */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Organisation-minimum exit checklist items
              <InfoTooltip text="These items form the mandatory minimum for every project's exit checklist in this phase. Project teams can add items but cannot remove these org-level minimums." />
            </label>
            <TagInput
              tags={checklistItems}
              onChange={onChecklistChange}
              placeholder="e.g. All PRs must reference a ticket number"
              disabled={!canEdit}
            />
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              These items are added to every project&apos;s exit checklist for this phase and cannot
              be removed by project teams.
            </p>

            {/* Suggested items collapsible */}
            {suggestions.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setShowSuggestions(v => !v)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                    color: 'var(--primary)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  {showSuggestions ? 'Hide suggestions' : 'Suggested items for ' + agentPhaseLabel}
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                    style={{ transform: showSuggestions ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 150ms' }}>
                    <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {showSuggestions && (
                  <div style={{
                    marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4,
                  }}>
                    {suggestions.map(item => {
                      const alreadyAdded = checklistItems.includes(item);
                      return (
                        <button
                          key={item}
                          type="button"
                          disabled={alreadyAdded || !canEdit}
                          onClick={() => { if (canEdit) addSuggestion(item); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '5px 10px', borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border)',
                            background: alreadyAdded ? 'var(--surface-alt)' : 'var(--surface)',
                            fontFamily: 'var(--font-body)', fontSize: 12,
                            color: alreadyAdded ? 'var(--text-secondary)' : 'var(--text)',
                            cursor: alreadyAdded ? 'default' : 'pointer',
                            opacity: alreadyAdded ? 0.6 : 1,
                            textAlign: 'left',
                          }}
                        >
                          <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 14 }}>+</span>
                          {item}
                          {alreadyAdded && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary)' }}>Added</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Starter set buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                Starter sets:
              </span>
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => { if (canEdit) applyStarterSet(HEALTHCARE_STARTER_SET); }}
                style={{
                  padding: '3px 10px', borderRadius: 4,
                  border: '1px solid var(--border)', background: 'var(--surface-alt)',
                  fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)',
                  cursor: canEdit ? 'pointer' : 'default',
                  opacity: canEdit ? 1 : 0.5,
                }}
              >
                Healthcare
              </button>
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => { if (canEdit) applyStarterSet(BFSI_STARTER_SET); }}
                style={{
                  padding: '3px 10px', borderRadius: 4,
                  border: '1px solid var(--border)', background: 'var(--surface-alt)',
                  fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)',
                  cursor: canEdit ? 'pointer' : 'default',
                  opacity: canEdit ? 1 : 0.5,
                }}
              >
                BFSI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function buildInitialState(savedData?: AgentConfigData): Map<AgentType, AgentConfigEntry> {
  const map = new Map<AgentType, AgentConfigEntry>();
  for (const agentType of ALL_AGENT_TYPES) {
    map.set(agentType, {
      agentType,
      isActive: true,
      systemPromptOverride: null,
      checklistDefaults: null,
    });
  }
  if (savedData) {
    for (const cfg of savedData.configs) {
      map.set(cfg.agentType, { ...cfg });
    }
  }
  return map;
}

export default function Step4AgentConfig({
  tenantId,
  onStepComplete,
  onSaved,
  savedData,
  submitLabel = 'Save & Continue →',
  canEdit = true,
  showModelPointer = false,
  showIntro = true,
}: Props) {
  const [configs, setConfigs] = useState<Map<AgentType, AgentConfigEntry>>(
    () => buildInitialState(savedData),
  );
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  // Load existing config on mount
  useEffect(() => {
    apiFetch<
      | { configured: false; defaults: Array<{ agentType: AgentType; isActive: boolean; systemPromptOverride: null; checklistDefaults: null }> }
      | { configured: true;  configs:  AgentConfigEntry[] }
    >(`/tenants/${tenantId}/agent-configs`)
      .then(result => {
        const map = new Map<AgentType, AgentConfigEntry>();
        const rows = result.configured ? result.configs : result.defaults;
        for (const row of rows) {
          if ((ALL_AGENT_TYPES as readonly string[]).includes(row.agentType)) {
            map.set(row.agentType as AgentType, {
              agentType:            row.agentType as AgentType,
              isActive:             row.isActive,
              systemPromptOverride: row.systemPromptOverride,
              checklistDefaults:    row.checklistDefaults,
            });
          }
        }
        for (const agentType of ALL_AGENT_TYPES) {
          if (!map.has(agentType)) {
            map.set(agentType, {
              agentType,
              isActive: true,
              systemPromptOverride: null,
              checklistDefaults: null,
            });
          }
        }
        setConfigs(map);
      })
      .catch(() => {});
  }, [tenantId]);

  const setActive = (agentType: AgentType, isActive: boolean) => {
    setConfigs(prev => {
      const next = new Map(prev);
      next.set(agentType, { ...next.get(agentType)!, isActive });
      return next;
    });
  };

  const setPrompt = (agentType: AgentType, value: string) => {
    setConfigs(prev => {
      const next = new Map(prev);
      next.set(agentType, { ...next.get(agentType)!, systemPromptOverride: value || null });
      return next;
    });
  };

  const setChecklist = (agentType: AgentType, items: string[]) => {
    setConfigs(prev => {
      const next = new Map(prev);
      next.set(agentType, { ...next.get(agentType)!, checklistDefaults: items.length ? items : null });
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Read-only guard: the submit button is hidden when canEdit=false, but a
    // form submit can still be triggered programmatically. The authoritative
    // gate is the server's requireRole('TENANT_ADMIN') on POST; this just
    // holds the read-only invariant at the JS layer too.
    if (!canEdit) return;
    setSaving(true);
    setSaveError('');
    setSavedFlash(false);
    try {
      // The full config is sent as-is. Per-agent model selection was removed
      // (CFGIA.B1 D11/D12 — the inert llm_* columns were dropped); models now
      // use governed defaults resolved server-side from agent_model_config.
      const configsArray = Array.from(configs.values());
      await apiFetch(`/tenants/${tenantId}/agent-configs`, {
        method: 'POST',
        body:   JSON.stringify({ configs: configsArray }),
      });
      const payload = { configs: configsArray };
      setSavedFlash(true);
      onSaved?.(payload);
      onStepComplete?.(payload);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save agent configuration');
    } finally {
      setSaving(false);
    }
  };

  const activePhaseRows = PHASE_ROWS.filter(r => configs.get(r.agentType)?.isActive);

  return (
    <form onSubmit={e => void handleSubmit(e)}>
      {/* Title + intro — omitted when showIntro is false (a surface with its
          own page-level <h1>, e.g. the Agents settings page, passes false to
          avoid a duplicate/skipped-level heading). Default true keeps the
          wizard step's heading. */}
      {showIntro && (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            Agent Configuration
          </h3>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>
            Choose which AI agents are active for your organisation and customise their behaviour.
          </p>
        </>
      )}

      {/* CFGIA.B1 D12 — wizard-only governed-model pointer. Replaces the former
          per-agent model dropdown (the inert tenant_agent_configs.llm_* columns
          were dropped). Static, writes nothing; the Settings Agents surface omits
          it (showModelPointer defaults false) — it has its own HelpPopover. */}
      {showModelPointer && (
        <div
          data-testid="agent-model-governed-pointer"
          style={{
            marginBottom: 28,
            padding: '12px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt)',
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            color: 'var(--text-secondary)',
          }}
        >
          Agent models use governed defaults — configure in Settings → Agents after setup.
        </div>
      )}

      {/* Phase activation grid */}
      <div style={{ marginBottom: 28 }}>
        <label style={labelStyle}>Phase activation</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--border)' }}>
          {PHASE_ROWS.map(row => {
            const entry  = configs.get(row.agentType);
            const active = entry?.isActive ?? true;
            return (
              <div
                key={row.agentType}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px',
                  background: active ? 'var(--surface)' : 'var(--surface-alt)',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: active ? 'var(--primary)' : 'var(--border)',
                  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
                  color: active ? '#fff' : 'var(--text-secondary)',
                  transition: 'background 150ms',
                }}>
                  {row.phase}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontFamily: 'var(--font-body)', fontSize: 14,
                    fontWeight: active ? 600 : 400,
                    color: active ? 'var(--text)' : 'var(--text-secondary)',
                    margin: 0, transition: 'color 150ms',
                  }}>
                    {row.agentLabel}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '1px 0 0' }}>
                    Phase {row.phase} — {row.phaseLabel}
                  </p>
                  {/* Inline explanation when agent is toggled OFF */}
                  {!active && !row.core && (
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--warning, #d97706)',
                      margin: '4px 0 0', fontStyle: 'italic',
                    }}>
                      Phase {row.phase} will run in manual mode — no AI assistance.
                    </p>
                  )}
                </div>

                {row.core ? (
                  <span
                    title="Core agents are required for BluePrint's orchestration pipeline and cannot be disabled."
                    style={{
                      fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                      padding: '2px 7px', borderRadius: 4,
                      background: 'rgba(124,58,237,0.08)', color: 'var(--primary)',
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                      cursor: 'help',
                    }}
                  >
                    Core
                  </span>
                ) : !active ? (
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                    padding: '2px 7px', borderRadius: 4,
                    background: 'rgba(217,119,6,0.08)', color: 'var(--warning, #d97706)',
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>
                    Manual
                  </span>
                ) : null}

                <Toggle
                  checked={active}
                  onChange={v => setActive(row.agentType, v)}
                  disabled={row.core || !canEdit}
                  title={row.core
                    ? 'Core agent — required for orchestration and cannot be disabled'
                    : active
                      ? 'Disable to run this phase manually without AI'
                      : 'Enable AI agent for this phase'}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Advanced accordion per active agent */}
      {activePhaseRows.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>Advanced configuration</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activePhaseRows.map(row => {
              const entry = configs.get(row.agentType)!;
              return (
                <AccordionSection
                  key={row.agentType}
                  label={row.agentLabel}
                  agentPhaseLabel={row.phaseLabel}
                  promptOverride={entry.systemPromptOverride ?? ''}
                  onPromptChange={v => setPrompt(row.agentType, v)}
                  checklistItems={entry.checklistDefaults ?? []}
                  onChecklistChange={v => setChecklist(row.agentType, v)}
                  canEdit={canEdit}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Error banner */}
      {saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {saveError}
        </div>
      )}

      {/* Saved flash */}
      {savedFlash && !saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: '#10b981',
        }}>
          Agent configuration saved.
        </div>
      )}

      {/* Submit — hidden in read-only mode (mirrors QAPolicyForm: the server's
          requireRole('TENANT_ADMIN') on POST is the authoritative gate). */}
      {canEdit ? (
        <button
          type="submit"
          disabled={saving}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 600,
            padding: '10px 24px',
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      ) : (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          Only tenant admins can edit the agent configuration.
        </p>
      )}
    </form>
  );
}
